import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import inventoryApi from '../api/inventoryApi';
import projectApi from '../api/projectApi';
import employeeApi from '../api/employeeApi';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import '../styles/InventoryList.css';
import { toast } from '../components/Toast';

function InventoryList({ defaultType }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(defaultType || '');
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: '',
    description: ''
  });
  const [formData, setFormData] = useState({
    itemCode: '',
    name: '',
    description: '',
    categoryId: '',
    unitOfMeasurement: 'UNIT',
    purchasePrice: '',
    currentValue: '',
    quantity: '1',
    minStockLevel: '0',
    status: 'AVAILABLE',
    currentLocation: '',
    serialNumber: '',
    notes: '',
    inventoryType: defaultType || 'ASSET'
  });
  const [assignData, setAssignData] = useState({
    itemId: '',
    branchId: '',
    responsibleEmployeeId: '',
    currentLocation: '',
    assignedProjectId: '',
    reason: '',
    notes: ''
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0
  });

  useEffect(() => {
    setTypeFilter(defaultType || '');
  }, [defaultType]);

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchBranches();
  }, [search, categoryFilter, statusFilter, typeFilter, pagination.currentPage]);

  // Fetch projects and employees when branch is selected in assign modal
  useEffect(() => {
    if (assignData.branchId) {
      fetchProjectsByBranch(assignData.branchId);
      fetchEmployeesByBranch(assignData.branchId);
    } else {
      // Clear lists when no branch selected
      setProjects([]);
      setEmployees([]);
    }
  }, [assignData.branchId]);

  const handleCreateCategory = async () => {
    if (!newCategoryForm.name.trim()) return;
    try {
      const response = await axios.post('/inventory/categories', {
        name: newCategoryForm.name.trim(),
        description: newCategoryForm.description.trim()
      });
      const data = response.data?.data || response.data;
      if (data && data.id) {
        setCategories(prev => [...prev, data]);
        await fetchCategories();
        setFormData(prev => ({
          ...prev,
          categoryId: String(data.id)
        }));
      }
      setShowNewCategoryModal(false);
      setNewCategoryForm({ name: '', description: '' });
    } catch (err) {
      console.error('Failed to create category:', err);
      alert(err.response?.data?.message || 'Failed to create category');
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getItems({
        search,
        categoryId: categoryFilter || undefined,
        status: statusFilter || undefined,
        inventoryType: typeFilter || undefined,
        page: pagination.currentPage,
        size: 20
      });
      setItems(response.data.items);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalItems: response.data.totalItems
      });
    } catch (err) {
      toast.error('Failed to fetch inventory items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await inventoryApi.getCategories();
      // Backend returns { data: [...] }, so we need to extract the array
      setCategories(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories', err);
      setCategories([]);
    }
  };

  const fetchProjectsByBranch = async (branchId) => {
    try {
      console.log('Fetching projects for branch:', branchId);
      const response = await projectApi.getProjects({ branchId, size: 100 });
      console.log('Full projects response:', response);
      console.log('response.data:', response.data);
      
      // Handle different response structures
      let projectList = [];
      
      if (response.data) {
        // Check for nested data.content (most specific first)
        if (response.data.data && response.data.data.content && Array.isArray(response.data.data.content)) {
          projectList = response.data.data.content;
        }
        // Check for direct content array (Spring pagination)
        else if (response.data.content && Array.isArray(response.data.content)) {
          projectList = response.data.content;
        }
        // Check for paginated response with data
        else if (response.data.data && Array.isArray(response.data.data)) {
          projectList = response.data.data;
        }
        // Check for direct projects array
        else if (response.data.projects && Array.isArray(response.data.projects)) {
          projectList = response.data.projects;
        }
        // Check if data itself is an array
        else if (Array.isArray(response.data)) {
          projectList = response.data;
        }
      }
      
      console.log('Extracted project list:', projectList, 'Length:', projectList.length);
      setProjects(projectList);
    } catch (err) {
      console.error('Failed to fetch projects', err);
      setProjects([]);
    }
  };

  const fetchEmployeesByBranch = async (branchId) => {
    try {
      console.log('Fetching employees for branch:', branchId);
      const response = await employeeApi.getEmployees({ branchId, size: 100 });
      console.log('Full employees response:', response);
      console.log('response.data:', response.data);
      
      // Handle different response structures
      let empList = [];
      
      if (response.data) {
        // Check for nested data.content (most specific first)
        if (response.data.data && response.data.data.content && Array.isArray(response.data.data.content)) {
          empList = response.data.data.content;
        }
        // Check for direct content array (Spring pagination)
        else if (response.data.content && Array.isArray(response.data.content)) {
          empList = response.data.content;
        }
        // Check for paginated response with data
        else if (response.data.data && Array.isArray(response.data.data)) {
          empList = response.data.data;
        }
        // Check for direct employees array
        else if (response.data.employees && Array.isArray(response.data.employees)) {
          empList = response.data.employees;
        }
        // Check if data itself is an array
        else if (Array.isArray(response.data)) {
          empList = response.data;
        }
      }
      
      console.log('Extracted employee list:', empList, 'Length:', empList.length);
      setEmployees(empList);
    } catch (err) {
      console.error('Failed to fetch employees', err);
      setEmployees([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data?.data || response.data || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches', err);
      setBranches([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Format payload to avoid sending empty strings for numeric/id fields
    const dataToSend = {
      ...formData,
      categoryId: formData.categoryId ? parseInt(formData.categoryId, 10) : null,
      purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
      currentValue: formData.currentValue ? parseFloat(formData.currentValue) : null,
      quantity: formData.quantity ? parseFloat(formData.quantity) : 0,
      minStockLevel: formData.minStockLevel ? parseFloat(formData.minStockLevel) : 0,
      maxStockLevel: formData.maxStockLevel ? parseFloat(formData.maxStockLevel) : null,
      branchId: formData.branchId ? parseInt(formData.branchId, 10) : null,
      responsibleEmployeeId: formData.responsibleEmployeeId ? parseInt(formData.responsibleEmployeeId, 10) : null,
      assignedProjectId: formData.assignedProjectId ? parseInt(formData.assignedProjectId, 10) : null,
    };

    try {
      if (currentItem) {
        await inventoryApi.updateItem(currentItem.id, dataToSend);
      } else {
        await inventoryApi.createItem(dataToSend);
      }
      setShowModal(false);
      fetchItems();
      resetForm();
    } catch (err) {
      alert('Failed to save item: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await inventoryApi.assignItem(assignData);
      setShowAssignModal(false);
      fetchItems();
      resetAssignForm();
    } catch (err) {
      alert('Failed to assign item: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = (id) => {
    setItemIdToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmOpen(false);
    if (!itemIdToDelete) return;
    try {
      await inventoryApi.deleteItem(itemIdToDelete);
      fetchItems();
    } catch (err) {
      alert('Failed to delete item: ' + (err.response?.data?.message || err.message));
    } finally {
      setItemIdToDelete(null);
    }
  };

  const openEditModal = (item) => {
    setCurrentItem(item);
    setFormData({
      itemCode: item.itemCode,
      name: item.name,
      description: item.description || '',
      categoryId: item.categoryId || '',
      unitOfMeasurement: item.unitOfMeasurement,
      purchasePrice: item.purchasePrice || '',
      currentValue: item.currentValue || '',
      quantity: item.quantity,
      minStockLevel: item.minStockLevel || '0',
      status: item.status,
      currentLocation: item.currentLocation || '',
      serialNumber: item.serialNumber || '',
      notes: item.notes || '',
      inventoryType: item.inventoryType || 'ASSET'
    });
    setShowModal(true);
  };

  const openAssignModal = (item) => {
    setAssignData({
      itemId: item.id,
      branchId: item.branchId || '',
      responsibleEmployeeId: item.responsibleEmployeeId || '',
      currentLocation: item.currentLocation || '',
      assignedProjectId: item.assignedProjectId || '',
      reason: '',
      notes: ''
    });
    setShowAssignModal(true);
  };

  const resetForm = () => {
    setCurrentItem(null);
    setFormData({
      itemCode: '',
      name: '',
      description: '',
      categoryId: '',
      unitOfMeasurement: 'UNIT',
      purchasePrice: '',
      currentValue: '',
      quantity: '1',
      minStockLevel: '0',
      status: 'AVAILABLE',
      currentLocation: '',
      serialNumber: '',
      notes: '',
      inventoryType: defaultType || 'ASSET'
    });
  };

  const resetAssignForm = () => {
    setAssignData({
      itemId: '',
      branchId: '',
      responsibleEmployeeId: '',
      currentLocation: '',
      assignedProjectId: '',
      reason: '',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      AVAILABLE: 'success',
      IN_USE: 'info',
      MAINTENANCE: 'warning',
      DAMAGED: 'danger',
      RETIRED: 'secondary'
    };
    return <span className={`badge badge-${statusColors[status] || 'secondary'}`}>{status}</span>;
  };

  return (
    <div className="inventory-list">
      <div className="page-header">
        <h1>{defaultType === 'ASSET' ? 'Asset Management' : defaultType === 'MATERIAL' ? 'Material Management' : 'Inventory Management'}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Add New {defaultType === 'ASSET' ? 'Asset' : defaultType === 'MATERIAL' ? 'Material' : 'Item'}
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="AVAILABLE">Available</option>
          <option value="IN_USE">In Use</option>
          <option value="MAINTENANCE">Maintenance</option>
          <option value="DAMAGED">Damaged</option>
          <option value="RETIRED">Retired</option>
        </select>
        {!defaultType && (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="ASSET">Assets</option>
            <option value="MATERIAL">Materials</option>
          </select>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Item Code</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  {defaultType !== 'MATERIAL' && <th>Location</th>}
                  {defaultType !== 'MATERIAL' && <th>Assigned To</th>}
                  {defaultType !== 'MATERIAL' && <th>Project</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} className={item.lowStock ? 'low-stock' : ''}>
                    <td>{item.itemCode}</td>
                    <td>{item.name}</td>
                    <td>{item.categoryName || '-'}</td>
                    <td>
                      {item.quantity} {item.unitOfMeasurement}
                      {item.lowStock && <span className="low-stock-badge">⚠️ Low</span>}
                    </td>
                    <td>{getStatusBadge(item.status)}</td>
                    {defaultType !== 'MATERIAL' && <td>{item.currentLocation || '-'}</td>}
                    {defaultType !== 'MATERIAL' && <td>{item.responsibleEmployeeName || '-'}</td>}
                    {defaultType !== 'MATERIAL' && <td>{item.assignedProjectName || '-'}</td>}
                    <td className="actions">
                      <button onClick={() => openEditModal(item)} className="btn-icon" title="Edit">
                        ✏️
                      </button>
                      {item.inventoryType !== 'MATERIAL' && (
                        <button onClick={() => openAssignModal(item)} className="btn-icon" title="Assign">
                          🔄
                        </button>
                      )}
                      <button onClick={() => navigate(`/inventory/${item.id}`)} className="btn-icon" title="View">
                        👁️
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="btn-icon" title="Delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              disabled={pagination.currentPage === 0}
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
            >
              Previous
            </button>
            <span>
              Page {pagination.currentPage + 1} of {pagination.totalPages}
            </span>
            <button
              disabled={pagination.currentPage >= pagination.totalPages - 1}
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{currentItem ? 'Edit Item' : 'Add New Item'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Item Code *</label>
                  <input
                    type="text"
                    value={formData.itemCode}
                    onChange={(e) => setFormData({ ...formData, itemCode: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => {
                      if (e.target.value === 'ADD_NEW') {
                        setShowNewCategoryModal(true);
                        setFormData(prev => ({ ...prev, categoryId: '' }));
                      } else {
                        setFormData({ ...formData, categoryId: e.target.value });
                      }
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                    <option value="ADD_NEW">+ Add New Category</option>
                  </select>
                </div>
                {!defaultType && (
                  <div className="form-group">
                    <label>Inventory Type *</label>
                    <select
                      value={formData.inventoryType}
                      onChange={(e) => setFormData({ ...formData, inventoryType: e.target.value })}
                      required
                    >
                      <option value="ASSET">Asset</option>
                      <option value="MATERIAL">Material</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Unit of Measurement *</label>
                  <input
                    type="text"
                    value={formData.unitOfMeasurement}
                    onChange={(e) => setFormData({ ...formData, unitOfMeasurement: e.target.value })}
                    required
                  />
                </div>
                {formData.inventoryType !== 'MATERIAL' && (
                  <div className="form-group">
                    <label>Quantity *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Min Stock Level</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.minStockLevel}
                    onChange={(e) => setFormData({ ...formData, minStockLevel: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{formData.inventoryType === 'MATERIAL' ? 'Unit Cost' : 'Purchase Price'}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.purchasePrice}
                    onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                  />
                </div>
                {formData.inventoryType !== 'MATERIAL' && (
                  <div className="form-group">
                    <label>Current Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.currentValue}
                      onChange={(e) => setFormData({ ...formData, currentValue: e.target.value })}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="AVAILABLE">Available</option>
                    <option value="IN_USE">In Use</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="DAMAGED">Damaged</option>
                    <option value="RETIRED">Retired</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Serial Number</label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="2"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {currentItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => { setShowAssignModal(false); resetAssignForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Assign / Reassign Inventory</h2>
            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label>Branch *</label>
                <select
                  value={assignData.branchId}
                  onChange={(e) => setAssignData({ 
                    ...assignData, 
                    branchId: e.target.value,
                    responsibleEmployeeId: '', // Reset employee when branch changes
                    assignedProjectId: '' // Reset project when branch changes
                  })}
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', marginTop: '4px' }}>Please select a branch first to see employees and projects</small>
              </div>
              <div className="form-group">
                <label>Responsible Employee (Optional)</label>
                <select
                  value={assignData.responsibleEmployeeId}
                  onChange={(e) => setAssignData({ ...assignData, responsibleEmployeeId: e.target.value })}
                  disabled={!assignData.branchId}
                >
                  <option value="">Select Employee</option>
                  {Array.isArray(employees) && employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.userName}</option>
                  ))}
                </select>
                {!assignData.branchId && (
                  <small style={{ color: '#9ca3af', marginTop: '4px' }}>Select a branch first</small>
                )}
                {assignData.branchId && Array.isArray(employees) && employees.length === 0 && (
                  <small style={{ color: '#6b7280', marginTop: '4px' }}>No employees found for selected branch</small>
                )}
              </div>
              <div className="form-group">
                <label>Assigned Project (Optional)</label>
                <select
                  value={assignData.assignedProjectId}
                  onChange={(e) => setAssignData({ ...assignData, assignedProjectId: e.target.value })}
                  disabled={!assignData.branchId}
                >
                  <option value="">Select Project</option>
                  {Array.isArray(projects) && projects.map(proj => (
                    <option key={proj.id} value={proj.id}>{proj.name}</option>
                  ))}
                </select>
                {!assignData.branchId && (
                  <small style={{ color: '#9ca3af', marginTop: '4px' }}>Select a branch first</small>
                )}
                {assignData.branchId && Array.isArray(projects) && projects.length === 0 && (
                  <small style={{ color: '#6b7280', marginTop: '4px' }}>No projects found for selected branch</small>
                )}
              </div>
              <div className="form-group">
                <label>Current Location (Optional)</label>
                <input
                  type="text"
                  value={assignData.currentLocation}
                  onChange={(e) => setAssignData({ ...assignData, currentLocation: e.target.value })}
                  placeholder="e.g., Warehouse A, Site Office"
                />
              </div>
              <div className="form-group">
                <label>Reason (Optional)</label>
                <input
                  type="text"
                  value={assignData.reason}
                  onChange={(e) => setAssignData({ ...assignData, reason: e.target.value })}
                  placeholder="Reason for assignment/transfer"
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={assignData.notes}
                  onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                  rows="3"
                  placeholder="Additional notes"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowAssignModal(false); resetAssignForm(); }} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showNewCategoryModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewCategoryModal(false);
            setNewCategoryForm({ name: '', description: '' });
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '450px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>New Category</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewCategoryModal(false);
                  setNewCategoryForm({ name: '', description: '' });
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Name *</label>
                <input
                  type="text"
                  value={newCategoryForm.name}
                  onChange={(e) => setNewCategoryForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Electrical Tools"
                  required
                />
              </div>
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newCategoryForm.description}
                  onChange={(e) => setNewCategoryForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe this category..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border, #e5e7eb)', background: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewCategoryModal(false);
                  setNewCategoryForm({ name: '', description: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateCategory}
                disabled={!newCategoryForm.name.trim()}
              >
                Save Category
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        title="Delete Inventory Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setItemIdToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default InventoryList;
